// utils/http.ts
import { openLogoutModal } from "../components/GlobalLogoutBus/globalLogoutBus";

const CSRF_HEADER = "X-CSRF-Token";
const DEFAULT_TIMEOUT_MS = 25_000;

let csrfToken: string | null = null;

export const clearCachedCsrf = () => { csrfToken = null; };
export const setCsrfToken = (t: string | null) => { csrfToken = t; };

export const forceLogoutNow = (message?: string) => {
  openLogoutModal({ message, autoConfirm: true });
};

// Prime once after login or app boot (if session likely exists)
export const primeCsrf = async (): Promise<void> => {
  try {
    const r = await apiFetch("/api/csrf-token", { method: "GET", credentials: "include" }, { skipLogoutOn401: true });
    if (!r.ok) throw new Error(`csrf token fetch failed: ${r.status}`);
    const data = await safeJson(r);
    if (data?.csrfToken) setCsrfToken(data.csrfToken);
  } catch {
    setCsrfToken(null); // allow lazy init fallback
  }
};

async function ensureCsrfForMutations(): Promise<void> {
  if (csrfToken) return;
  await primeCsrf(); // lazy init
}

function isSafeMethod(method: string) {
  const m = method.toUpperCase();
  return m === "GET" || m === "HEAD" || m === "OPTIONS";
}

function makeAbort(timeoutMs?: number) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs ?? DEFAULT_TIMEOUT_MS);
  return { signal: controller.signal, cancel: () => clearTimeout(id) };
}

/** Parse JSON safely; if content-type looks like HTML, return null to signal SPA fallback */
async function safeJson(resp: Response): Promise<any | null> {
  const ct = resp.headers.get("content-type") || "";
  if (!ct.toLowerCase().includes("application/json")) {
    // Probably got index.html from SPA fallback or a file
    return null;
  }
  // handle 204 No Content
  if (resp.status === 204) return null;
  return resp.json();
}

/** Ensure we didn't get HTML back for an endpoint that should be JSON */
function assertLikelyJson(resp: Response) {
  const ct = (resp.headers.get("content-type") || "").toLowerCase();
  if (resp.ok && ct.includes("text/html")) {
    throw new Error("Unexpected HTML response (possible SPA fallback)");
  }
}

/**
 * Main wrapper
 */

export async function apiFetch(
    input: RequestInfo | URL,
    init: RequestInit = {},
    options?: {
      retryOnCsrf?: boolean;      // default true
      timeoutMs?: number;         // default 25s
      autoLogoutOn401?: boolean;  // default true (still shows logout modal)
      throwOn401?: boolean;       // default true
      noCsrf?: boolean;
      skipLogoutOn401?: boolean;  // skip force logout popup for auth flow 401s
    }
) {
  const headers = new Headers(init.headers || {});
  const method = (init.method ?? "GET").toUpperCase();
  const safe = isSafeMethod(method);

  const { signal, cancel } = makeAbort(options?.timeoutMs);
  const cfg: RequestInit = {
    credentials: "include",
    ...init,
    headers,
    signal,
  };

  try {
    // Attach CSRF for mutating calls unless explicitly disabled
    if (!safe && !options?.noCsrf) {
      await ensureCsrfForMutations();
      if (csrfToken) headers.set(CSRF_HEADER, csrfToken);
    }

    let resp = await fetch(input, cfg);

    // 401 → session expired
    if (resp.status === 401) {
      // Only show force logout popup if not explicitly disabled and not in auth flow
      if ((options?.autoLogoutOn401 ?? true) && !options?.skipLogoutOn401) {
        await forceLogoutNow("Your session has expired. Please sign in again.");
      }
      if (options?.throwOn401 ?? true) {
        throw new Error("Unauthorized");
      }
      // Let caller handle 401
      return resp;
    }

    // 403 → treat as CSRF mismatch only if CSRF is in play
    if (
        resp.status === 403 &&
        !safe &&
        !options?.noCsrf &&
        (options?.retryOnCsrf ?? true)
    ) {
      await primeCsrf();
      if (csrfToken) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set(CSRF_HEADER, csrfToken);
        resp = await fetch(input, { ...cfg, headers: retryHeaders });

        if (resp.ok) return resp;
        if (resp.status === 401) {
          if ((options?.autoLogoutOn401 ?? true) && !options?.skipLogoutOn401) {
            await forceLogoutNow("Your session has expired. Please sign in again.");
          }
          if (options?.throwOn401 ?? true) {
            throw new Error("Unauthorized");
          }
          return resp;
        }
      }
      await forceLogoutNow("Security error.");
      throw new Error("Forbidden");
    }

    return resp;
  } finally {
    cancel();
  }
}


/**
 * Convenience helpers for JSON requests
 */

export async function apiGetJson<T = any>(
    url: string,
    init: RequestInit = {},
    options?: Parameters<typeof apiFetch>[2]
): Promise<T> {
  const resp = await apiFetch(url, { ...init, method: "GET" }, options);
  assertLikelyJson(resp);
  const data = await safeJson(resp);
  if (data === null && resp.ok) {
    // Treat as valid empty result for 204s; otherwise throw for SPA fallback
    if (resp.status === 204) return null as unknown as T;
    throw new Error("Expected JSON but received non-JSON content");
  }
  return data as T;
}

export async function apiPostJson<T = any>(
    url: string,
    body: any,
    init: RequestInit = {},
    options?: Parameters<typeof apiFetch>[2]
): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const resp = await apiFetch(
      url,
      { ...init, method: "POST", headers, body: JSON.stringify(body) },
      options
  );
  // Not all POSTs return JSON — try and only assert if 2xx with non-empty
  const data = await safeJson(resp);
  if (resp.ok && data === null && resp.status !== 204) {
    // caller can decide; we'll just return null
    return null as unknown as T;
  }
  return data as T;
}
